/** Endpoints do control plane, servidos em /cp com o slug injetado pelo Caddy. */

export interface Branding {
  slug: string; name: string; tracker_host: string;
  /** `#rrggbb` definido pelo cliente; ausente usa a cor da plataforma. */
  cor?: string | null;
  /** Nome da família, da lista fechada que o servidor devolve em `fontes`. */
  fonte?: string | null;
  fontes?: string[];
  /** Relativa ao painel do próprio cliente, servida pelo Caddy em /marca. */
  logo_url?: string | null;
}

export interface MarcaIn {
  brand_color?: string | null;
  brand_font?: string | null;
  /** base64 puro ou data URL — o servidor descarta o prefixo. */
  logo_base64?: string;
  remove_logo?: boolean;
}

export interface Projection {
  visible: boolean;           // false abaixo de 80% — a promessa é "ilimitado"
  pct: number;
  dias: number;               // histórico efetivo; é o mesmo número invertido
  retencao_contratada: number;
  dispositivos: number;
  estado: "OK" | "WARN" | "FIFO" | "THROTTLE" | "DEVICE_HOLD";
  simulador: { gb: number; preco: number; dispositivos: number; fica_em: number }[];
}

/** Faixas de "temperatura" — ocupação do espaço DEPOIS da compra. */
export type Temperatura = "critico" | "quente" | "morno" | "bom" | "folgado";

export interface Cobranca {
  mensalidade: number;
  prorata: number;
  prorata_dias: number;
  /** O total da PRÓXIMA fatura, já com base + bloco + pró-rata. */
  primeira_fatura: number;
  cobra_a_partir_de: string;   // ISO
}

export interface OpcaoEspaco {
  gb: number;
  preco: number;
  espaco_total_gb: number;
  pct: number;
  dias: number;
  temperatura: Temperatura;
  temperatura_rotulo: string;
  /** Quantos veículos ainda cabem depois desta compra. */
  cabem_mais: number;
  suporta_dispositivos: number;
  cobranca: Cobranca;
}

/** Ampliação já pedida, esperando o pagamento do boleto. */
export interface AmpliacaoPendente {
  gb: number;
  valor: number;
  mensalidade: number;
  boleto_url: string | null;
  vencimento: string | null;   // ISO
}

export interface Espaco {
  dispositivos: number;
  espaco_gb: number;
  espaco_base_gb: number;
  espaco_extra_gb: number;
  usado_gb: number;
  projetado_gb: number;
  pct: number;
  dias: number;
  retencao_contratada: number;
  estado: Projection["estado"];
  temperatura: Temperatura;
  temperatura_rotulo: string;
  /** MiB que UM equipamento gera por mês. */
  consumo_mib_mes: number;
  consumo_gb_mes_frota: number;
  /** false = fator da plataforma, não desta frota. Muda o texto da tela. */
  consumo_medido: boolean;
  mensalidade_atual: number;
  mensalidade_base: number;
  dia_fatura: number;
  fatura_aberta: boolean;
  opcoes: OpcaoEspaco[];
  recomendado: number | null;
  /** Quando existe, a tela mostra o boleto em vez de vender de novo. */
  pendente: AmpliacaoPendente | null;
  /** false = o faturamento está fora; some o botão em vez de prometer. */
  pode_contratar: boolean;
  /** Para onde a ampliação foi (ADR-0016): a tela manda o cliente ao lugar certo. */
  ampliar_em?: string;
  historico: { gb: number; preco: number; prorata: number; prorata_dias: number;
               cobra_a_partir_de: string; em: string }[];
}

export interface Connection {
  host: string;
  /** IP do servidor. Só para equipamento que não aceita hostname (ADR-0005). */
  ip: string | null;
  equipamentos: { modelo: string; porta: number; protocolo: string }[];
  aviso: string;
}

const MOCK = import.meta.env.DEV && import.meta.env.VITE_MOCK !== "0";

async function cp<T>(path: string): Promise<T> {
  if (MOCK) return mock(path) as T;
  const r = await fetch(`/cp/api/self${path}`, { headers: { accept: "application/json" } });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json() as Promise<T>;
}

function mock(path: string): unknown {
  if (path.startsWith("/geocode")) {
    const RUAS = [
      "Av. Paulista, 1578 · Bela Vista · São Paulo",
      "Marginal Tietê, km 22 · Vila Leopoldina · São Paulo",
      "Rod. Anhanguera, km 31 · Cajamar",
      "Av. do Estado, 4560 · Mooca · São Paulo",
      "Rua Sete de Setembro, 210 · Centro · Guarulhos",
      "Av. Brig. Faria Lima, 3900 · Itaim Bibi · São Paulo",
      "Rod. Régis Bittencourt, km 279 · Embu das Artes",
      "Av. Nações Unidas, 12901 · Brooklin · São Paulo",
      "Rua da Consolação, 2200 · Consolação · São Paulo",
      "Av. Cruzeiro do Sul, 1800 · Santana · São Paulo",
    ];
    const lat = Number(new URLSearchParams(path.split("?")[1]).get("lat") ?? 0);
    return { endereco: RUAS[Math.abs(Math.round(lat * 10000)) % RUAS.length], origem: "cache" };
  }
  if (path === "/branding")
    return { slug: "acme", name: "Acme Rastreamento",
             tracker_host: "gps-acme.apprastreio.com.br",
             cor: "#1c6ba8", fonte: null, logo_url: null,
             fontes: ["Outfit", "Inter", "Roboto", "Open Sans", "Montserrat", "Poppins",
                      "Nunito Sans", "Source Sans 3", "Lato", "Work Sans", "Manrope", "Rubik"] };
  if (path === "/projection")
    return {
      visible: true, pct: 146.5, dias: 31, retencao_contratada: 45,
      dispositivos: 300, estado: "FIFO",
      simulador: [
        { gb: 5, preco: 194.9, dispositivos: 256, fica_em: 65.1 },
        { gb: 10, preco: 389.9, dispositivos: 512, fica_em: 41.9 },
        { gb: 25, preco: 964.9, dispositivos: 1280, fica_em: 20.2 },
      ],
    };
  if (path === "/space") {
    const opcao = (gb: number, preco: number, pct: number, cabem: number,
                   temp: Temperatura, rotulo: string): OpcaoEspaco => ({
      gb, preco, espaco_total_gb: 4 + gb, pct, dias: pct <= 100 ? 45 : Math.round(45 * 100 / pct),
      temperatura: temp, temperatura_rotulo: rotulo, cabem_mais: cabem,
      suporta_dispositivos: 300 + cabem,
      cobranca: { mensalidade: preco, prorata: Math.round(preco * 27 / 30 * 100) / 100,
                  prorata_dias: 27, primeira_fatura: Math.round((169.9 + preco + preco * 27 / 30) * 100) / 100,
                  cobra_a_partir_de: "2026-10-20" },
    });
    return {
      dispositivos: 300, espaco_gb: 4, espaco_base_gb: 4, espaco_extra_gb: 0,
      usado_gb: 3.94, projetado_gb: 5.86, pct: 146.5, dias: 31,
      retencao_contratada: 45, estado: "FIFO",
      temperatura: "critico", temperatura_rotulo: "ainda estoura",
      consumo_mib_mes: 13.3, consumo_gb_mes_frota: 3.91, consumo_medido: false,
      mensalidade_atual: 169.9, mensalidade_base: 169.9,
      dia_fatura: 20, fatura_aberta: false,
      opcoes: [
        opcao(5, 194.9, 65.1, 160, "bom", "confortável"),
        opcao(10, 389.9, 41.9, 416, "folgado", "com folga"),
        opcao(25, 964.9, 20.2, 1184, "folgado", "com folga"),
        opcao(50, 1924.9, 10.9, 2464, "folgado", "com folga"),
        opcao(100, 3844.9, 5.6, 5024, "folgado", "com folga"),
      ],
      recomendado: 5,
      pendente: null,
      pode_contratar: true,
      historico: [],
    } satisfies Espaco;
  }
  if (path === "/protocols")
    return {
      host: "gps-acme.apprastreio.com.br",
      ip: "179.127.11.17",
      protocolos: [
        { protocolo: "gt06", rotulo: "Concox / Sinotrack / GT06N", transporte: "tcp",
          porta: 20623, porta_traccar: 5023, ativo: true },
        { protocolo: "suntech", rotulo: "Suntech ST300 / ST310 / ST4300", transporte: "tcp+udp",
          porta: 20611, porta_traccar: 5011, ativo: false },
        { protocolo: "osmand", rotulo: "App de celular / Traccar Client", transporte: "tcp",
          porta: 20655, porta_traccar: 5055, ativo: false },
        { protocolo: "h02", rotulo: "Diversos chineses", transporte: "tcp+udp",
          porta: 20613, porta_traccar: 5013, ativo: false },
        { protocolo: "teltonika", rotulo: "Teltonika FMB / FMC", transporte: "tcp+udp",
          porta: 20627, porta_traccar: 5027, ativo: false },
        { protocolo: "watch", rotulo: "Relógios rastreadores", transporte: "tcp",
          porta: 20693, porta_traccar: 5093, ativo: false },
      ],
    };
  if (path === "/connection")
    return {
      host: "gps-acme.apprastreio.com.br",
      ip: "179.127.11.17",
      equipamentos: [
        { modelo: "Suntech ST300 / ST310 / ST4300", porta: 20611, protocolo: "suntech" },
        { modelo: "Concox / Sinotrack / GT06N", porta: 20623, protocolo: "gt06" },
        { modelo: "App de celular / Traccar Client", porta: 20655, protocolo: "osmand" },
      ],
      aviso: "Configure o SERVIDOR pelo nome sempre que o equipamento aceitar: é o que " +
             "permite trocar de servidor sem mexer no equipamento. O IP é para quem não " +
             "aceita nome, e custa visita física numa troca.",
    };
  return {};
}

export interface Protocolo {
  protocolo: string;
  rotulo: string;
  transporte: string;
  /** A porta DESTE cliente. Não muda nunca — ligar/desligar só publica ou não. */
  porta: number;
  porta_traccar: number;
  ativo: boolean;
}

export interface Protocolos {
  host: string;
  /**
   * IP do servidor do cliente, para o equipamento que não aceita hostname.
   * Anulável: tenant sem servidor atribuído não tem IP a informar.
   */
  ip: string | null;
  protocolos: Protocolo[];
}

export interface Geo { endereco: string | null; origem: "cache" | "upstream" | "indisponível"; }

/** Escrita nunca passa pelo mock: salvar e não salvar não podem parecer iguais. */
async function cpEscrever<T>(path: string, corpo: unknown, metodo = "PUT"): Promise<T> {
  // ⚠️ GET e DELETE não podem levar corpo — o `fetch` recusa com TypeError, e a
  // tela mostraria "falha de rede" para uma leitura que nunca saiu.
  const semCorpo = metodo === "GET" || metodo === "DELETE" || corpo === undefined;
  const r = await fetch(`/cp/api/self${path}`, {
    method: metodo,
    credentials: "same-origin",         // o cookie do Traccar prova quem é
    headers: semCorpo
      ? { accept: "application/json" }
      : { "content-type": "application/json", accept: "application/json" },
    ...(semCorpo ? {} : { body: JSON.stringify(corpo) }),
  });
  if (!r.ok) {
    // Falha de servidor não devolve JSON: o corpo é "Internal Server Error" em
    // texto puro, e mostrar "500" na tela não diz nada a quem está usando.
    const d = await r.json().catch(() => null);
    throw new Error(d?.detail ?? mensagemHttp(r.status));
  }
  return r.json() as Promise<T>;
}

function mensagemHttp(status: number): string {
  if (status === 401) return "Sua sessão expirou. Entre de novo para salvar.";
  if (status === 403) return "Só um administrador pode alterar a identidade visual.";
  if (status === 413) return "A imagem é grande demais.";
  if (status >= 500) return "O servidor falhou ao salvar. Tente de novo; se persistir, fale com o suporte.";
  return "Não foi possível salvar.";
}

// ── Faturamento B2C (ADR-0015) ───────────────────────────────────────────────

export interface CampoGateway {
  nome: string; rotulo: string; segredo?: boolean; ajuda?: string; obrigatorio?: boolean;
  /** `"arquivo"` vira upload; o conteúdo viaja em base64 no mesmo campo. */
  tipo?: "arquivo";
}
export interface ProvedorCatalogo {
  kind: string; rotulo: string; campos: CampoGateway[]; ambientes: string[];
}
export interface Gateway {
  id: number;
  kind: string;
  rotulo: string;
  ambiente: "production" | "sandbox";
  ativo: boolean;
  verificado_em: string | null;
  ultimo_erro: string | null;
  /**
   * Prefixo truncado de cada campo secreto — `{ api_key: "$aact_YTU5YTE…" }`.
   *
   * ⚠️ NUNCA a credencial. O campo do formulário volta vazio de propósito, e a
   * dica é só para o tenant reconhecer qual chave está guardada.
   */
  dicas: Record<string, string>;
  criado_em: string;
}
export interface GatewaysResposta {
  gateways: Gateway[];
  catalogo: ProvedorCatalogo[];
  /** Falso quando o servidor não tem o cofre configurado; a tela não deixa salvar. */
  cofre_pronto: boolean;
}
export interface Verificacao {
  ok: boolean; conta?: string; erro?: string; indisponivel?: boolean;
}

export interface Macro { nome: string; descricao: string; exemplo: string }
export interface ModeloMensagem {
  momento: string;
  rotulo: string;
  quando: string;
  canal: "whatsapp" | "email";
  assunto: string;
  texto: string;
  ativo: boolean;
  /** Verdadeiro quando o texto na tela é o nosso padrão, não escolha do tenant. */
  de_fabrica: boolean;
  alterado_em: string | null;
}
export interface ModelosResposta {
  modelos: ModeloMensagem[];
  macros: Macro[];
  exemplo: Record<string, string>;
}

/**
 * O que o tenant vende, e por quanto.
 *
 * Ciclo, carência, multa, juros, desconto e veículos ficam no CONTRATO — são
 * negociação com um cliente, não característica do produto (migração 018).
 */
export interface Plano {
  id: number; nome: string; valor: number; ativo: boolean;
  contratos: number; adesao: number; ordem: number;
}
export interface Contrato {
  id: number;
  /**
   * Id do **grupo** no Traccar do tenant. Neste produto, cliente é um grupo —
   * é ele que tem razão social, CNPJ e endereço; usuário é só login.
   */
  cliente_id: number;
  plano_id: number | null; plano: string | null;
  valor: number; dia_vencimento: number;
  status: "ACTIVE" | "SUSPENDED" | "CANCELED";
  inicio: string; fim: string | null; primeiro_vencimento: string | null;
  suspenso_em: string | null; observacao: string; descricao: string;
  cobrar_adesao: boolean; veiculos: number; motivo_cancelamento: string;
  em_aberto: number;
  /** Ciclo em meses: 1 mensal, 3 trimestral, 12 anual. */
  ciclo_meses: number; carencia_dias: number;
  /** Padrão de mercado no Brasil: 2% de multa e 1% de juros ao mês. */
  multa_percent: number; juros_mes_percent: number;
  desconto_valor: number; desconto_dias: number;
}
export interface AjustesFaturamento {
  habilitado: boolean; dias_antes_gerar: number; dias_depois_avisar: number;
  dias_depois_suspender: number; avisar_whatsapp: boolean; avisar_email: boolean;
}
export interface Anexo {
  id: number; nome: string; tipo: string; bytes: number;
  observacao: string; enviado_em: string;
}

export interface ContratosResposta {
  contratos: Contrato[]; planos: Plano[]; ajustes: AjustesFaturamento;
}

export interface CanalCatalogo {
  kind: "whatsapp" | "email"; rotulo: string; campos: CampoGateway[];
}
export interface Canal {
  id: number; kind: "whatsapp" | "email"; rotulo: string;
  /**
   * Os campos que NÃO são segredo, com o valor inteiro — o tenant precisa ver
   * qual servidor e qual remetente estão configurados para conferir.
   */
  campos: Record<string, string>;
  /** Prefixo truncado dos campos secretos. NUNCA o valor. */
  dicas: Record<string, string>;
  ativo: boolean; verificado_em: string | null; ultimo_erro: string | null;
}
export interface CanaisResposta {
  canais: Canal[]; catalogo: CanalCatalogo[]; cofre_pronto: boolean;
}

export interface Fatura {
  id: number; contrato_id: number | null; cliente_id: number;
  descricao: string; valor: number; vencimento: string;
  status: "PENDING" | "PAID" | "OVERDUE" | "CANCELED";
  competencia: string | null; pago_em: string | null; valor_pago: number | null;
  url_fatura: string; url_boleto: string; pix: string; linha_digitavel: string;
  multa_percent: number; juros_mes_percent: number;
  desconto_valor: number; desconto_dias: number;
  ultimo_erro: string | null; criada_em: string;
  /** Fatura que esta substitui numa reemissão. */
  substitui: number | null;
  /** 1 = original, 2 = 2ª via, … */
  via: number;
  /** Principal + encargos acumulados até hoje. Sugestão, nunca cobrança. */
  atualizado: { dias: number; multa: number; juros: number; total: number } | null;
  /** Calculado no servidor para "em atraso" significar o mesmo nas duas telas. */
  dias_atraso: number;
  /**
   * Só na resposta da **baixa manual**: o que ficou por fazer do lado do
   * provedor. Ou ele não sabe registrar recebimento (Mercado Pago), ou não
   * respondeu — e nos dois casos sobra trabalho para o tenant.
   */
  aviso?: string;
}

/** Uma ação manual que mexeu no dinheiro desta fatura (migração 022). */
export interface EventoFatura {
  acao: "baixa_manual" | "cancelamento" | "reemissao";
  quem: string;
  detalhe: string;
  quando: string;
}
export interface ResumoFaturas {
  abertas: number; valor_aberto: number; vencidas: number;
  valor_vencido: number; recebido_no_mes: number;
}
export interface FaturasResposta { faturas: Fatura[]; resumo: ResumoFaturas }

export interface Devedor {
  cliente_id: number; contrato_id: number | null;
  nome: string; email: string; celular: string;
  faturas: number; total: number; mais_antiga: string | null; dias_atraso: number;
  suspenso: boolean;
  /** Quantos dias até o corte. `null` = já suspenso, ou faturamento desligado. */
  dias_para_suspender: number | null;
  f1_30: number; f31_60: number; f61_90: number; f90: number;
}
export interface Inadimplencia {
  devedores: Devedor[];
  faixas: { chave: string; rotulo: string; valor: number }[];
  total: number; clientes: number; suspensos: number;
  dias_para_suspender: number | null; gerado_em: string;
}
export interface FaturamentoResumo {
  habilitado: boolean; resumo: ResumoFaturas;
  inadimplentes: number; suspensos: number; contratos_ativos: number;
}

export interface SenhaRedefinida {
  enviada: boolean;
  /** Por que o WhatsApp não saiu. Só quando `enviada` é falso. */
  motivo?: string;
  /**
   * ⚠️ Só vem quando o envio FALHOU — e aí a tela precisa mostrá-la.
   *
   * A senha nova já está valendo nesse momento: se ela não aparecer para o
   * administrador, a pessoa fica trancada para fora e ninguém mais sabe a
   * senha. Quando o envio dá certo, o servidor não devolve: ela já chegou a
   * quem precisava, e repetir na tela só aumenta por onde pode vazar.
   */
  senha?: string;
  usuario: string;
  nome: string;
  celular: string;
}

export const plan = {
  geocode: (lat: number, lon: number) =>
    cp<Geo>(`/geocode?lat=${lat.toFixed(6)}&lon=${lon.toFixed(6)}`),
  branding: () => cp<Branding>("/branding"),
  projection: () => cp<Projection>("/projection"),
  connection: () => cp<Connection>("/connection"),
  protocolos: () => cp<Protocolos>("/protocols"),
  espaco: () => cp<Espaco>("/space"),

  // ── faturamento B2C ────────────────────────────────────────────────────
  gateways: () => cpEscrever<GatewaysResposta>("/gateways", undefined, "GET"),
  gatewaySalvar: (corpo: { kind: string; campos: Record<string, string>;
                           ambiente: string; rotulo: string; ativar: boolean },
                  id?: number) =>
    cpEscrever<{ id: number; verificacao: Verificacao }>(
      id ? `/gateways/${id}` : "/gateways", corpo, id ? "PUT" : "POST"),
  gatewayVerificar: (id: number) =>
    cpEscrever<Verificacao>(`/gateways/${id}/verificar`, {}, "POST"),
  gatewayRemover: (id: number) =>
    cpEscrever<{ ok: boolean }>(`/gateways/${id}`, undefined, "DELETE"),

  /**
   * Sorteia uma senha para o usuário e avisa por WhatsApp.
   *
   * ⚠️ Não serve para a própria senha, e o servidor recusa — a tela nem
   * oferece o botão. Quem troca a senha é o administrador que clicou, com o
   * cookie dele.
   */
  redefinirSenhaUsuario: (id: number) =>
    cpEscrever<SenhaRedefinida>(`/usuarios/${id}/redefinir-senha`, {}, "POST"),

  modelos: () => cpEscrever<ModelosResposta>("/modelos-mensagem", undefined, "GET"),
  modeloSalvar: (corpo: { momento: string; canal: string; assunto: string;
                          texto: string; ativo: boolean }) =>
    cpEscrever<{ ok: boolean }>("/modelos-mensagem", corpo, "PUT"),
  inadimplencia: () => cpEscrever<Inadimplencia>("/inadimplencia", undefined, "GET"),
  faturamentoResumo: () =>
    cpEscrever<FaturamentoResumo>("/faturamento-resumo", undefined, "GET"),

  faturas: (status = "", clienteId = 0) =>
    cpEscrever<FaturasResposta>(
      `/faturas?status=${encodeURIComponent(status)}&cliente_id=${clienteId}`,
      undefined, "GET"),
  /** As faturas de quem está logado. O filtro é do servidor, pelo cookie. */
  minhasFaturas: () =>
    cpEscrever<{ faturas: Fatura[] }>("/minhas-faturas", undefined, "GET"),
  faturaAvulsa: (corpo: { cliente_id: number; descricao: string;
                          valor: number; vencimento: string }) =>
    cpEscrever<Fatura>("/faturas", corpo, "POST"),
  faturaBaixar: (id: number) =>
    cpEscrever<Fatura>(`/faturas/${id}/baixar`, {}, "POST"),
  faturaReemitir: (id: number, corpo: { vencimento: string; valor: number | null }) =>
    cpEscrever<Fatura>(`/faturas/${id}/reemitir`, corpo, "POST"),
  faturaCancelar: (id: number) =>
    cpEscrever<{ ok: boolean }>(`/faturas/${id}`, undefined, "DELETE"),
  /** Quem deu baixa, cancelou ou reemitiu — e quando. */
  faturaHistorico: (id: number) =>
    cpEscrever<{ historico: EventoFatura[] }>(`/faturas/${id}/historico`,
                                              undefined, "GET"),

  canais: () => cpEscrever<CanaisResposta>("/canais", undefined, "GET"),
  canalSalvar: (corpo: { kind: string; campos: Record<string, string>;
                         ativo: boolean; testar_para: string }) =>
    cpEscrever<{ ok: boolean; verificacao: Verificacao }>("/canais", corpo, "PUT"),
  canalRemover: (kind: string) =>
    cpEscrever<{ ok: boolean }>(`/canais/${kind}`, undefined, "DELETE"),

  planos: () => cpEscrever<{ planos: Plano[] }>("/planos", undefined, "GET"),
  planoSalvar: (corpo: Omit<Plano, "id" | "contratos">, id?: number) =>
    cpEscrever<{ id: number }>(id ? `/planos/${id}` : "/planos", corpo, id ? "PUT" : "POST"),
  planoRemover: (id: number) =>
    cpEscrever<{ ok: boolean }>(`/planos/${id}`, undefined, "DELETE"),

  contratos: () => cpEscrever<ContratosResposta>("/contratos", undefined, "GET"),
  contratoSalvar: (corpo: {
    cliente_id: number; plano_id: number | null; valor: number | null;
    dia_vencimento: number; descricao: string; observacao: string;
    fim: string | null; primeiro_vencimento: string | null;
    cobrar_adesao: boolean; veiculos: number;
    ciclo_meses: number; carencia_dias: number;
    multa_percent: number; juros_mes_percent: number;
    desconto_valor: number; desconto_dias: number;
  }, id?: number) =>
    cpEscrever<{ id: number }>(id ? `/contratos/${id}` : "/contratos", corpo,
                               id ? "PUT" : "POST"),
  contratoCancelar: (id: number, motivo: string) =>
    cpEscrever<{ ok: boolean }>(
      `/contratos/${id}?motivo=${encodeURIComponent(motivo)}`, undefined, "DELETE"),

  anexos: (contratoId: number) =>
    cpEscrever<{ anexos: Anexo[] }>(`/contratos/${contratoId}/anexos`, undefined, "GET"),
  /** Multipart: base64 infla 33% e um contrato assinado tem megabytes. */
  anexoEnviar: async (contratoId: number, arquivo: File, observacao = "") => {
    const fd = new FormData();
    fd.append("arquivo", arquivo);
    fd.append("observacao", observacao);
    const r = await fetch(`/cp/api/self/contratos/${contratoId}/anexos`, {
      method: "POST", credentials: "same-origin", body: fd,
    });
    if (!r.ok) {
      const d = await r.json().catch(() => null);
      throw new Error(d?.detail ?? mensagemHttp(r.status));
    }
    return r.json() as Promise<Anexo>;
  },
  anexoRemover: (id: number) =>
    cpEscrever<{ ok: boolean }>(`/anexos/${id}`, undefined, "DELETE"),
  /** O download passa pelo servidor, que confere a sessão — nunca é estático. */
  anexoUrl: (id: number) => `/cp/api/self/anexos/${id}`,

  ajustesSalvar: (corpo: AjustesFaturamento) =>
    cpEscrever<{ ok: boolean }>("/faturamento-ajustes", corpo, "PUT"),

  modeloRestaurar: (momento: string, canal: string) =>
    cpEscrever<{ subject: string; body: string }>(
      `/modelos-mensagem/${momento}/${canal}`, undefined, "DELETE"),

  /**
   * Contrata um bloco. Manda só o TAMANHO — o preço é lido do catálogo no
   * servidor. Preço vindo da tela sairia por R$ 0,01 para quem abre o DevTools.
   *
   * Avisa a aplicação ao terminar, como o `salvarMarca`: a faixa de aviso vive
   * no App e leria a projeção antiga por mais cinco minutos, continuando a pedir
   * que o cliente amplie o espaço que ele acabou de ampliar.
   */
  async contratar(gb: number): Promise<Espaco> {
    const e = await cpEscrever<Espaco>("/space/order", { gb }, "POST");
    window.dispatchEvent(new CustomEvent<Espaco>("espaco:atualizado", { detail: e }));
    return e;
  },

  /** Manda a lista INTEIRA de ligados: o servidor republica as portas uma vez. */
  salvarProtocolos: (ativos: string[]) =>
    cpEscrever<Protocolos>("/protocols", { ativos }),

  upgrade: () => cpEscrever<Upgrade>("/upgrade", undefined, "GET"),

  pedidosUpgrade: () =>
    cpEscrever<PedidoUpgrade[]>("/upgrade/pedidos", undefined, "GET"),

  /** ⚠️ Registra o pedido. NÃO libera recurso — quem aplica é a fase 6. */
  pedirUpgrade: (resource: "vcpu" | "ram" | "disk", blocks = 1) =>
    cpEscrever<PedidoUpgrade>("/upgrade/pedido", { resource, blocks }, "POST"),

  /**
   * Muda a janela de histórico.
   *
   * ⚠️ `confirmoPerderDias` é obrigatório quando a mudança apaga histórico, e
   * precisa ser o número EXATO que o servidor informou. É o que impede um
   * clique distraído de dropar meses de partição — que não volta.
   */
  retencao: (dias: number, confirmoPerderDias?: number) =>
    cpEscrever<{ atual: number; mudou: boolean; dias_apagados: number }>(
      "/retencao",
      confirmoPerderDias === undefined
        ? { dias } : { dias, confirmo_perder_dias: confirmoPerderDias },
      "POST"),

  /**
   * Salva a identidade e AVISA a aplicação.
   *
   * Sem o aviso, o cabeçalho e a barra lateral seguem com o logotipo que
   * carregaram ao abrir a página: o cliente troca a marca, vê a prévia mudar e
   * conclui, com razão, que não salvou.
   */
  async salvarMarca(m: MarcaIn): Promise<Branding> {
    const b = await cpEscrever<Branding>("/branding", m);
    window.dispatchEvent(new CustomEvent<Branding>("marca:atualizada", { detail: b }));
    return b;
  },
};

/** Mensagem ao cliente por estado. Nunca "erro": é degradação prevista. */

// ── Upgrade (fase 5, ADR-0016) ─────────────────────────────────────────────
export interface Medidor { usado: number; teto: number; pct: number }
export interface BlocoPreco { bloco: number; preco: number; unidade: string }
export interface PedidoUpgrade {
  id: number; resource: "vcpu" | "ram" | "disk"; blocks: number;
  block_size: number; total: number;
  state: "PENDING" | "CONFIRMED" | "CANCELLED"; created_at?: string;
}
export interface Upgrade {
  contratado: { vcpu: number; ram_mb: number; disco_gb: number };
  /** `false` = ainda não há série medida. NÃO é o mesmo que consumo zero. */
  medido: boolean;
  medidores: { cpu: Medidor | null; ram: Medidor | null; disco: Medidor };
  cpu_media: number | null;
  dispositivos: number;
  blocos: { vcpu: BlocoPreco; ram: BlocoPreco; disk: BlocoPreco };
  retencao: {
    atual: number; minimo: number; maximo: number;
    dias_de_historico: number;
    perda_por_opcao: Record<string, number>;
  };
}

export const ESTADO_MSG: Record<Projection["estado"], { titulo: string; texto: string } | null> = {
  OK: null,
  WARN: {
    titulo: "Espaço quase no limite",
    texto: "Ampliar agora mantém os 45 dias de histórico.",
  },
  FIFO: {
    titulo: "Histórico reduzido",
    texto: "Seu volume de dados passou do espaço contratado, então os dias mais " +
           "antigos vão saindo para dar lugar aos novos. O rastreamento ao vivo " +
           "não é afetado.",
  },
  THROTTLE: {
    titulo: "Gravação espaçada",
    texto: "Para preservar o histórico, as posições passaram a ser gravadas a " +
           "cada 2 minutos. Alarmes e eventos continuam sendo registrados.",
  },
  DEVICE_HOLD: {
    titulo: "Novos dispositivos pausados",
    texto: "Os equipamentos já cadastrados continuam funcionando normalmente. " +
           "Ampliar o espaço libera a inclusão de novos na hora.",
  },
};
